import React, { useEffect, useState } from 'react';
import { getUserCases, getAppUser, handleStatus} from '../../functions/utils';
import {
  getBestandImports,
  getBestandFiles,
  deleteBestandImport,
  handleBestandImport,
  handleBestandFinal,
  deleteBankBestandById,
} from '../../functions/bestand';
import { getCookie } from '../../functions/utils';
import '../../css/ImportComponent.css';
import ImportHeaderComponent from '../page/ImportHeaderComponent';
import DropdownComponent from '../utilities/DropdownComponent';
import LoaderComponent from '../utilities/LoaderComponent';
import FileDropComponent from '../utilities/FileDropComponent';
import ConfirmationComponent from '../utilities/ConfirmationComponent';
import StatusComponent from '../utilities/StatusComponent';
import BestandLogo from '../../images/BestandLogo.png';
import { useTranslations } from '../utilities/TranslationContext';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark, faChevronDown, faCircleCheck, faCircleXmark, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

library.add(faChevronDown, faXmark, faCircleCheck, faCircleXmark, faTriangleExclamation);

// Mirrors the backend's bestandKey (bestand.service.ts): statement_id is authoritative when
// present, since two statements can legitimately share the same period (e.g. an interim vs.
// final statement for the same day) without being duplicates.
function bestandDupKey(f) {
  if (f.statement_id) return `id:${f.iban}|${f.statement_id}`;
  if (f.opening_balance_date && f.closing_balance_date) return `period:${f.iban}|${f.opening_balance_date}|${f.closing_balance_date}`;
  return null;
}

const CamtComponent = () => {
  const { getTranslation, language } = useTranslations();

  const [userCases, setUserCases] = useState([]);
  const [imports, setImports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusUpdates, setStatusUpdates] = useState([]);

  // Upload form state
  const [selectedCase, setSelectedCase] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingFilesMetadata, setPendingFilesMetadata] = useState([]);
  const [pendingJrnid, setPendingJrnid] = useState('');   // BIC[0:4]+IBAN[-4:], e.g. ABNA9999
  const [pendingYear, setPendingYear] = useState('');     // extracted from file dates
  const [isUploading, setIsUploading] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);
  const [fileDropKey, setFileDropKey] = useState(0);

  // Config history expansion
  const [expandedConfigId, setExpandedConfigId] = useState(null);
  const [configFiles, setConfigFiles] = useState({});

  // Confirm delete
  const [confirmDeleteId, setConfirmDeleteId] = useState(false);
  const [confirmDeleteBankId, setConfirmDeleteBankId] = useState(false);
  const [confirmDeleteFileGroup, setConfirmDeleteFileGroup] = useState(false);
  const [deletingDuplicatesId, setDeletingDuplicatesId] = useState(null);

  // File-group expansion (per uploaded file, within a config's file history)
  const [expandedFileGroups, setExpandedFileGroups] = useState({});

  const showUploadForm = pendingFiles.length > 0;
  const canImport = showUploadForm && selectedCase && !isUploading;

  useEffect(() => { init(); }, []);

  async function init() {
    const appUser = await getAppUser();
    if (!appUser) return setTimeout(init, 300);
    const cases = await getUserCases();
    if (cases) setUserCases(cases);
    const impBestanden = await getBestandImports(cases);
    setImports(Array.isArray(impBestanden) ? impBestanden : []);
    setIsLoading(false);
  }

  async function reload() {
    const imps = await getBestandImports(userCases);
    setImports(Array.isArray(imps) ? imps : []);
  }

  // ── File handling ──────────────────────────────────────────────────────────────

  function extractFileMetadata(xml) {
    const ibanMatch = xml.match(/<IBAN>([^<]+)<\/IBAN>/i);
    // BIC was renamed BICFI in newer ISO 20022 schema versions (camt.053.001.08+).
    const bicMatch  = xml.match(/<BIC>([^<]+)<\/BIC>/i) || xml.match(/<BICFI>([^<]+)<\/BICFI>/i);
    const iban = ibanMatch ? ibanMatch[1].trim() : '';
    const bic  = bicMatch  ? bicMatch[1].trim()  : '';
    // A file can bundle many <Stmt> blocks (e.g. one per day) — scan all of them so the
    // preview period spans the whole file, not just the first statement's day.
    const prcdDates = [...xml.matchAll(/<Cd>PRCD<\/Cd>[\s\S]*?<Dt><Dt>(\d{4}-\d{2}-\d{2})/g)].map(m => m[1]);
    const clbdDates = [...xml.matchAll(/<Cd>CLBD<\/Cd>[\s\S]*?<Dt><Dt>(\d{4}-\d{2}-\d{2})/g)].map(m => m[1]);
    const frDt = prcdDates.length ? prcdDates.reduce((min, d) => (d < min ? d : min)) : '';
    const toDt = clbdDates.length ? clbdDates.reduce((max, d) => (d > max ? d : max)) : '';
    // Year must come from the statement period, not the file's CreDtTm (export timestamp) —
    // a generic "first date in the file" match picks up the header's creation date instead.
    // Prefer the latest date: for a file spanning a year boundary (e.g. a rolling 12-month
    // export), most of its content — and the "current" period — belongs to the later year.
    const year = toDt ? toDt.slice(0, 4) : (frDt ? frDt.slice(0, 4) : '');
    const txCount = (xml.match(/<Ntry\b/g) || []).length;
    return { iban, bic, year, frDt, toDt, txCount };
  }

  async function onFilesDropped(newFiles) {
    if (newFiles.length === 0) return;
    const metaArr = [];
    for (const file of newFiles) {
      try {
        const xml = await file.text();
        const { bic, iban, year, frDt, toDt, txCount } = extractFileMetadata(xml);
        metaArr.push({
          year,
          jrnid: (bic.slice(0, 4) + iban.slice(-4)).toUpperCase(),
          frDt,
          toDt,
          txCount,
        });
      } catch {
        metaArr.push({ year: '', jrnid: '', frDt: '', toDt: '', txCount: 0 });
      }
    }
    const isFirst = pendingFiles.length === 0;
    setPendingFiles(prev => [...prev, ...newFiles]);
    setPendingFilesMetadata(prev => [...prev, ...metaArr]);
    if (isFirst && metaArr.length > 0) {
      setPendingJrnid(metaArr[0].jrnid);
      setPendingYear(metaArr[0].year);
    }
  }

  function cancelUpload() {
    setPendingFiles([]);
    setPendingFilesMetadata([]);
    setPendingJrnid('');
    setPendingYear('');
    setSelectedCase(false);
    setDropdownKey(k => k + 1);
    setFileDropKey(k => k + 1);
  }

  function removePendingFile(idx) {
    const files = pendingFiles.filter((_, i) => i !== idx);
    const meta = pendingFilesMetadata.filter((_, i) => i !== idx);
    setPendingFiles(files);
    setPendingFilesMetadata(meta);
    setFileDropKey(k => k + 1);
    if (files.length === 0) {
      setPendingJrnid('');
      setPendingYear('');
      setDropdownKey(k => k + 1);
    }
  }

  // ── Import ─────────────────────────────────────────────────────────────────────

  async function runImport() {
    if (!selectedCase) return;
    setIsUploading(true);
    await handleStatus(updateStatus, 'bestand', true);

    const entiteit = `${selectedCase}_1`;
    const totalFiles = pendingFiles.length;
    let lastConfigId = null;
    const impidTotals = new Map(); // impid → { impid, entiteit, transactionCount }
    let fileIndex = 0;
    for (const file of pendingFiles) {
      fileIndex += 1;
      let xmlContent;
      try { xmlContent = await file.text(); }
      catch { continue; }

      // Per-file extraction — fall back to values parsed from the first file
      const { bic, iban, year } = extractFileMetadata(xmlContent);
      const jrnid = (bic.slice(0, 4) + iban.slice(-4)).toUpperCase() || pendingJrnid;
      const fileYear = year || pendingYear;

      try {
        const result = await handleBestandImport({
          entiteit,
          year: fileYear,
          jrnid,
          fileName: file.name,
          xmlContent,
          fileIndex,
          totalFiles,
        });
        console.log(result);
        // A file spanning a fiscal-year boundary comes back as one entry per year it touched.
        for (const entry of result?.results ?? []) {
          lastConfigId = entry.configId;
          const impid = entry.impid;
          const prev = impidTotals.get(impid) ?? { impid, entiteit, transactionCount: 0 };
          impidTotals.set(impid, { ...prev, transactionCount: prev.transactionCount + (entry.transactionCount ?? 0) });
        }
      } catch (e) {}
    }
    console.log(impidTotals);
    // Every impid touched during this batch runs through handleFinal, even with 0 net-new
    // transactions, so executeBankProcedure still sets datasetjaar for that year/impid.
    const finalEntries = Array.from(impidTotals.values());

    // Always call handleFinal, even with zero entries, so the server always reports a terminal status.
    try { await handleBestandFinal(finalEntries); } catch (e) {}

    setIsUploading(false);
    cancelUpload();
    await reload();

    if (lastConfigId) {
      setExpandedConfigId(lastConfigId);
      const files = await getBestandFiles(lastConfigId);
      setConfigFiles(prev => ({ ...prev, [lastConfigId]: files }));
    }
  }

  // ── Config expansion (file history) ──────────────────────────────────────────

  async function toggleConfig(imp) {
    if (expandedConfigId === imp.id) {
      setExpandedConfigId(null);
    } else {
      setExpandedConfigId(imp.id);
      if (imp.impid > 0 && !configFiles[imp.id]) {
        const files = await getBestandFiles(imp.id);
        setConfigFiles(prev => ({ ...prev, [imp.id]: files }));
      }
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────────

  async function handleDeleteBank(entiteit, bankbestand_id, configId) {
    const case_id = entiteit.substring(0, entiteit.indexOf('_'));
    await deleteBankBestandById(case_id, getCookie('factview_access'), bankbestand_id);
    setConfirmDeleteBankId(false);
    const files = await getBestandFiles(configId);
    setConfigFiles(prev => ({ ...prev, [configId]: files }));
    await reload();
  }

  async function handleDeleteFileGroup(entiteit, bankbestandIds, configId) {
    const case_id = entiteit.substring(0, entiteit.indexOf('_'));
    for (const id of bankbestandIds) {
      await deleteBankBestandById(case_id, getCookie('factview_access'), id);
    }
    setConfirmDeleteFileGroup(false);
    const files = await getBestandFiles(configId);
    setConfigFiles(prev => ({ ...prev, [configId]: files }));
    await reload();
  }

  async function deleteDuplicates(imp) {
    setDeletingDuplicatesId(imp.id);
    const existing = configFiles[imp.id] ?? [];
    const case_id = imp.entiteit.substring(0, imp.entiteit.indexOf('_'));
    const seen = new Set();
    for (const f of existing) {
      const key = bestandDupKey(f);
      if (!key) continue;
      if (seen.has(key)) {
        await deleteBankBestandById(case_id, getCookie('factview_access'), f.id);
      } else {
        seen.add(key);
      }
    }
    const files = await getBestandFiles(imp.id);
    setConfigFiles(prev => ({ ...prev, [imp.id]: files }));
    setDeletingDuplicatesId(null);
    await reload();
  }

  async function handleDelete(id) {
    await deleteBestandImport(id);
    setConfirmDeleteId(false);
    if (expandedConfigId === id) setExpandedConfigId(null);
    await reload();
  }
  
  useEffect(() => {  
    if(statusUpdates.length > 0){
      evaluateStatus();
    }
  },[statusUpdates]);
  
  async function evaluateStatus(){
    if(statusUpdates.length > 0 && (statusUpdates[statusUpdates.length - 1].status == 0 || statusUpdates[statusUpdates.length - 1].status == 2)){
      setStatusUpdates([]);
    }
  } 
  
  // Updates status messages array
  const updateStatus = (message) => {
    setStatusUpdates(message);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────

  function getCaseName(entiteit) {
    const id = entiteit.substring(0, entiteit.indexOf('_'));
    const c = userCases.find(uc => String(uc.id) === String(id));
    return c ? c.name : entiteit;
  }

  function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function formatStamp(stamp) {
    if (!stamp) return '-';
    return new Date(stamp).toLocaleString(`${language?.language_code}-${language?.country_code}`, {
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    });
  }

  return (
    <div className="import-container">
      <ImportHeaderComponent
        title="CAMT"
        logo={BestandLogo}
        subtitle={getTranslation('import_BestandSubtitle')}
      />
      <div className="import-component-container">
        <div className="import-component-content">
          <div className="import-content">

            {/* ── Upload section – always visible ── */}
            <div className="import-create-basic">
              <FileDropComponent key={fileDropKey} onFilesAccepted={onFilesDropped} showFileList={false} />
            </div>

            {/* ── Toggled inputs – only when a file is dropped ── */}
            {showUploadForm && !isUploading && (
              <div className="import-create-basic">
                <div className="case-content">
                  <p>{getTranslation('import_FVCase')}*</p>
                  <DropdownComponent
                    key={dropdownKey}
                    options={userCases.map(c => ({ label: c.name, value: c.id, type: 'case' }))}
                    defaultValue={{ label: getTranslation('import_SelectCase'), value: null, type: null }}
                    onSelect={o => o.type === 'case' && setSelectedCase(o.value)}
                    className="case"
                  />
                </div>
              </div>
            )}

            {/* ── Pending files list ── */}
            {showUploadForm && !isUploading && pendingFiles.length > 0 && (
              <div className="import-results-wrapper">
                <table className="import-results-table">
                  <thead>
                    <tr>
                      <th>Bestandsnaam</th>
                      <th>{getTranslation('import_Year')}</th>
                      <th>Dagboek</th>
                      <th>Periode</th>
                      <th>Transacties</th>
                      <th>Grootte</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingFiles.map((f, i) => {
                      const meta = pendingFilesMetadata[i] || {};
                      const periode = meta.frDt && meta.toDt ? `${meta.frDt} – ${meta.toDt}` : '-';
                      return (
                        <tr key={i} className="file-row-pending">
                          <td>{f.name}</td>
                          <td>{meta.year || '-'}</td>
                          <td>{meta.jrnid || '-'}</td>
                          <td>{periode}</td>
                          <td>{meta.txCount != null ? meta.txCount : '-'}</td>
                          <td>{formatSize(f.size)}</td>
                          <td>
                            <button className="file-drop-item-remove" onClick={() => removePendingFile(i)} type="button">
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Upload loader ── */}
            {isUploading && <LoaderComponent isLoading paddingTop="10px" />}

            {/* ── Menubar ── */}
            {showUploadForm && !isUploading && (
              <div className="import-menubar">
                <button className={`import-btn submit-btn hover${!canImport ? ' disabled' : ''}`} disabled={!canImport} onClick={runImport}>
                  {getTranslation('import_Import')}
                </button>
                <button className="import-btn cancel-btn hover" onClick={cancelUpload}>
                  {getTranslation('import_Cancel')}
                </button>
              </div>
            )}

            {/* ── Config list – always visible ── */}
            <LoaderComponent isLoading={isLoading} paddingTop="20px" />
            {!isLoading && imports.length > 0 && (
              <div className="list-content">
                <div className="camt-list-scroll">
                <table border="1">
                  <tbody>
                    <tr className="list-header">
                      <td className="list-expand"></td>
                      <td className="list-case">{getTranslation('import_Case')}</td>
                      <td className="list-year">{getTranslation('import_Year')}</td>
                      <td>Dagboek</td>
                      <td className="list-stamp no-wrap">{getTranslation('import_LastUpdated')}</td>
                      <td className="edit"></td>
                    </tr>
                    {imports.map((imp, index) => {
                      const isExpanded = expandedConfigId === imp.id;
                      return (
                        <React.Fragment key={imp.id}>
                          <tr value={index} onClick={() => toggleConfig(imp)}>
                            <td className="td-expand">
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`config-expand-icon${isExpanded ? ' rotated' : ''}`}
                              />
                            </td>
                            <td>{getCaseName(imp.entiteit)}</td>
                            <td>{imp.year}</td>
                            <td>{imp.jrnid}</td>
                            <td>{formatStamp(imp.stamp)}</td>
                            <td className="td-editor">
                              <div className="delete-list list-editor-option hover" onClick={e => { e.stopPropagation(); setConfirmDeleteId(imp.id); }}>
                                <FontAwesomeIcon icon={faXmark} />
                              </div>
                              {confirmDeleteId === imp.id && (
                                <ConfirmationComponent
                                  message="Weet u zeker dat u deze configuratie wilt verwijderen?"
                                  onCancel={() => setConfirmDeleteId(false)}
                                  onConfirm={() => handleDelete(imp.id)}
                                />
                              )}
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr className="config-files-row">
                              <td colSpan={6}>
                                {(() => {
                                  const existing = configFiles[imp.id] ?? [];
                                  if (imp.impid === 0 || existing.length === 0) {
                                    return <p style={{ padding: '8px' }}>Geen bestanden geïmporteerd.</p>;
                                  }
                                  const hasDuplicates = existing.some(f => bestandDupKey(f)) && (() => {
                                    const counts = {};
                                    existing.forEach(f => {
                                      const key = bestandDupKey(f);
                                      if (key) counts[key] = (counts[key] || 0) + 1;
                                    });
                                    return Object.values(counts).some(c => c > 1);
                                  })();
                                  if (deletingDuplicatesId === imp.id) {
                                    return (
                                      <div className="import-results-wrapper">
                                        <LoaderComponent isLoading paddingTop="10px" />
                                        <p style={{ textAlign: 'center', padding: '4px 8px 8px' }}>Duplicaten verwijderen...</p>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="import-results-wrapper">
                                      {hasDuplicates && (
                                        <div className="import-dup-menubar">
                                          <button className="import-btn delete-dup-button cancel-btn hover" onClick={e => { e.stopPropagation(); deleteDuplicates(imp); }}>
                                            Verwijder duplicaten
                                          </button>
                                        </div>
                                      )}
                                      <table className="import-results-table">
                                        <thead>
                                          <tr>
                                            <th></th>
                                            <th>Bestandsnaam</th>
                                            <th>Type</th>
                                            <th>Periode</th>
                                            <th>Transacties</th>
                                            <th></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(() => {
                                            const periodCounts = existing.reduce((acc, f) => {
                                              const key = bestandDupKey(f);
                                              if (key) acc[key] = (acc[key] || 0) + 1;
                                              return acc;
                                            }, {});
                                            const isDuplicate = f => { const key = bestandDupKey(f); return key && periodCounts[key] > 1; };

                                            // One uploaded file can contain many <Stmt> blocks (e.g. one per day),
                                            // each stored as its own row — group them back into one row per file here.
                                            const groupsMap = new Map();
                                            existing.forEach(f => {
                                              if (!groupsMap.has(f.file_name)) groupsMap.set(f.file_name, []);
                                              groupsMap.get(f.file_name).push(f);
                                            });
                                            const groups = Array.from(groupsMap.entries()).map(([file_name, rows]) => {
                                              const dupCount = rows.filter(isDuplicate).length;
                                              const openingDates = rows.map(r => r.opening_balance_date).filter(Boolean).sort();
                                              const closingDates = rows.map(r => r.closing_balance_date).filter(Boolean).sort();
                                              return {
                                                file_name,
                                                rows,
                                                file_type: rows[0]?.file_type,
                                                dupCount,
                                                opening: openingDates[0],
                                                closing: closingDates[closingDates.length - 1],
                                                transactionCount: rows.reduce((sum, r) => sum + (r.transaction_count || 0), 0),
                                                ids: rows.map(r => r.id),
                                              };
                                            }).sort((a, b) => (b.dupCount > 0) - (a.dupCount > 0));

                                            return groups.map(group => {
                                              const groupKey = `${imp.id}|${group.file_name}`;
                                              const isGroupExpanded = !!expandedFileGroups[groupKey];
                                              return (
                                                <React.Fragment key={group.file_name}>
                                                  <tr
                                                    className={`file-row-existing${group.dupCount > 0 ? ' file-row-duplicate' : ''}`}
                                                    onClick={() => setExpandedFileGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                                                  >
                                                    <td>
                                                      {group.dupCount > 0
                                                        ? <FontAwesomeIcon icon={faTriangleExclamation} className="duplicate-icon" title="Bevat periode(s) die al eerder zijn geïmporteerd" />
                                                        : <FontAwesomeIcon icon={faCircleCheck} />
                                                      }
                                                    </td>
                                                    <td>
                                                      {group.rows.length > 1 && (
                                                        <FontAwesomeIcon
                                                          icon={faChevronDown}
                                                          className={`config-expand-icon${isGroupExpanded ? ' rotated' : ''}`}
                                                          style={{ marginRight: '6px' }}
                                                        />
                                                      )}
                                                      {group.file_name}
                                                      {group.rows.length > 1 && <span className="file-row-count"> ({group.rows.length} periodes)</span>}
                                                      {group.dupCount > 0 && <span className="duplicate-label">{group.dupCount} Duplicaat{group.dupCount > 1 ? 'en' : ''}</span>}
                                                    </td>
                                                    <td>{group.file_type}</td>
                                                    <td>{group.opening && group.closing ? `${group.opening} – ${group.closing}` : '-'}</td>
                                                    <td>{group.transactionCount}</td>
                                                    <td className="td-editor">
                                                      <div className="delete-list list-editor-option hover" onClick={e => { e.stopPropagation(); setConfirmDeleteFileGroup(groupKey); }}>
                                                        <FontAwesomeIcon icon={faXmark} />
                                                      </div>
                                                      {confirmDeleteFileGroup === groupKey && (
                                                        <ConfirmationComponent
                                                          message={`Weet u zeker dat u dit bestand (${group.rows.length} periode(s)) en bijbehorende banktransacties wilt verwijderen?`}
                                                          onCancel={() => setConfirmDeleteFileGroup(false)}
                                                          onConfirm={() => handleDeleteFileGroup(imp.entiteit, group.ids, imp.id)}
                                                        />
                                                      )}
                                                    </td>
                                                  </tr>
                                                  {isGroupExpanded && group.rows.map(f => {
                                                    const dup = isDuplicate(f);
                                                    return (
                                                      <tr key={f.id} className={`file-row-existing file-row-nested${dup ? ' file-row-duplicate' : ''}`}>
                                                        <td>
                                                          {dup
                                                            ? <FontAwesomeIcon icon={faTriangleExclamation} className="duplicate-icon" title="Dubbel bestand: zelfde periode al geïmporteerd" />
                                                            : <FontAwesomeIcon icon={faCircleCheck} />
                                                          }
                                                        </td>
                                                        <td>
                                                          {f.statement_id || (f.opening_balance_date && f.closing_balance_date ? `${f.opening_balance_date} – ${f.closing_balance_date}` : f.file_name)}
                                                          {dup && <span className="duplicate-label">Duplicaat</span>}
                                                        </td>
                                                        <td>{f.file_type}</td>
                                                        <td>{f.opening_balance_date && f.closing_balance_date ? `${f.opening_balance_date} – ${f.closing_balance_date}` : '-'}</td>
                                                        <td>{f.transaction_count}</td>
                                                        <td className="td-editor">
                                                          <div className="delete-list list-editor-option hover" onClick={e => { e.stopPropagation(); setConfirmDeleteBankId(f.id); }}>
                                                            <FontAwesomeIcon icon={faXmark} />
                                                          </div>
                                                          {confirmDeleteBankId === f.id && (
                                                            <ConfirmationComponent
                                                              message="Weet u zeker dat u dit bankbestand en bijbehorende banktransacties wilt verwijderen?"
                                                              onCancel={() => setConfirmDeleteBankId(false)}
                                                              onConfirm={() => handleDeleteBank(imp.entiteit, f.id, imp.id)}
                                                            />
                                                          )}
                                                        </td>
                                                      </tr>
                                                    );
                                                  })}
                                                </React.Fragment>
                                              );
                                            });
                                          })()}
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                <StatusComponent
                  data={statusUpdates} 
                  currentProgress={(statusUpdates.length>0)?statusUpdates[statusUpdates.length-1]:{"progress": 0, "status": 0}}
                  reloadList={()=>{reload()}}
                />                                
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default CamtComponent;
